import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiBaseURL } from "../api/client";

const SECTION_LABELS = {
  executive_summary: "Executive Summary",
  positive_signals: "What Is Working Well",
  current_risks: "Current Risks and Negative Signals",
  incident_learnings: "Incident Learnings",
  process_findings_by_area: "Process Findings by Area",
  operational_readiness: "Operational Readiness",
  documentation_and_handover: "Documentation and Handover",
  recommendations: "Recommendations",
  recommendation_matrix: "Recommendation Matrix / Action Plan",
  consultant_notes: "Consultant Notes"
};

const SECTION_ORDER = [
  "executive_summary",
  "positive_signals",
  "current_risks",
  "incident_learnings",
  "process_findings_by_area",
  "operational_readiness",
  "documentation_and_handover",
  "recommendations",
  "recommendation_matrix",
  "consultant_notes"
];

function decodeSections(report) {
  const raw = report?.edited_sections_json || report?.report_json?.sections || {};
  const source = typeof raw === "object" && raw ? raw : {};
  const take = (...keys) =>
    keys
      .map((key) => String(source[key] || "").trim())
      .filter(Boolean)
      .join("\n\n");

  return {
    executive_summary: take("executive_summary"),
    positive_signals: take("positive_signals"),
    current_risks: take("current_risks", "security_and_access", "performance_and_reliability"),
    incident_learnings: take("incident_learnings", "incident_landscape"),
    process_findings_by_area: take(
      "process_findings_by_area",
      "business_context",
      "platform_hosting_assessment",
      "wordpress_core_theme_plugin_health",
      "integrations_and_dependencies"
    ),
    operational_readiness: take("operational_readiness", "governance_and_maintenance_readiness"),
    documentation_and_handover: take("documentation_and_handover"),
    recommendations: take("recommendations", "prioritized_recommendations_30_60_90"),
    recommendation_matrix: take("recommendation_matrix"),
    consultant_notes: take("consultant_notes", "analyst_comments"),
    section_meta: typeof source.section_meta === "object" && source.section_meta ? source.section_meta : {},
    risk_score: Number(source.risk_score || 0)
  };
}

function riskProfile(score) {
  if (score >= 75) return { label: "High Risk", tone: "high" };
  if (score >= 45) return { label: "Moderate Risk", tone: "medium" };
  return { label: "Lower Risk", tone: "low" };
}

function deriveRiskScoreFromMeta(sectionMeta = {}) {
  const ratings = Object.values(sectionMeta || {})
    .flatMap((group) => Object.values(group || {}))
    .map((item) => Number(item?.rating))
    .filter((value) => Number.isFinite(value));
  if (!ratings.length) return null;
  return Math.round(ratings.reduce((sum, value) => sum + value, 0) / ratings.length);
}

function normalizeDate(value) {
  const parsed = value ? new Date(String(value).replace(" ", "T")) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
}

function parseStructuredBlocks(value) {
  const text = String(value || "").replace(/\r\n/g, "\n").trim();
  if (!text) return [];

  const rawBlocks = text.includes("### ")
    ? text.split(/\n(?=### )/).map((part) => part.trim()).filter(Boolean)
    : [text];

  return rawBlocks.map((block, index) => {
    const lines = block.split("\n");
    const first = lines[0] || "";
    const hasHeading = /^###\s+/.test(first.trim());
    const heading = hasHeading
      ? first.replace(/^###\s+/, "").trim()
      : `${index + 1}. Details`;

    const bodyLines = hasHeading ? lines.slice(1) : lines;
    const items = [];
    let paragraphBuffer = [];

    const flushParagraph = () => {
      const textValue = paragraphBuffer.join(" ").replace(/\s+/g, " ").trim();
      if (textValue) {
        items.push({ type: "p", text: textValue });
      }
      paragraphBuffer = [];
    };

    bodyLines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        flushParagraph();
        return;
      }
      const unordered = trimmed.match(/^[-*]\s+(.+)$/);
      if (unordered) {
        flushParagraph();
        items.push({ type: "li", ordered: false, text: unordered[1].trim() });
        return;
      }
      const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
      if (ordered) {
        flushParagraph();
        items.push({ type: "li", ordered: true, text: ordered[1].trim() });
        return;
      }
      paragraphBuffer.push(trimmed);
    });

    flushParagraph();

    if (!items.length && hasHeading) {
      items.push({ type: "p", text: "No details entered for this heading yet." });
    }

    return {
      heading: heading || `${index + 1}. Details`,
      items
    };
  });
}

function renderBlockItems(items, keyPrefix) {
  const output = [];
  let listBuffer = [];
  let listOrdered = false;

  const flushList = (flushKey) => {
    if (!listBuffer.length) return;
    if (listOrdered) {
      output.push(
        <ol key={`${flushKey}-ol`}>
          {listBuffer.map((item, index) => <li key={`${flushKey}-oli-${index}`}>{item.text}</li>)}
        </ol>
      );
    } else {
      output.push(
        <ul key={`${flushKey}-ul`}>
          {listBuffer.map((item, index) => <li key={`${flushKey}-uli-${index}`}>{item.text}</li>)}
        </ul>
      );
    }
    listBuffer = [];
  };

  items.forEach((item, index) => {
    if (item.type === "li") {
      if (!listBuffer.length) {
        listOrdered = Boolean(item.ordered);
      }
      if (listOrdered !== Boolean(item.ordered)) {
        flushList(`${keyPrefix}-${index}`);
        listOrdered = Boolean(item.ordered);
      }
      listBuffer.push(item);
      return;
    }

    flushList(`${keyPrefix}-${index}`);
    output.push(<p key={`${keyPrefix}-p-${index}`}>{item.text}</p>);
  });

  flushList(`${keyPrefix}-end`);
  return output;
}

function TabbedSection({ blocks, sectionKey }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= blocks.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, blocks.length]);

  if (!blocks.length) return null;

  return (
    <div className="public-report-tabbed">
      <div className="public-report-tablist" role="tablist" aria-label={`${sectionKey}-tabs`}>
        {blocks.map((block, index) => (
          <button
            key={`${sectionKey}-tab-${index}`}
            type="button"
            className={`report-tab ${activeIndex === index ? "active" : ""}`}
            onClick={() => setActiveIndex(index)}
            role="tab"
            aria-selected={activeIndex === index}
            title={block.heading || `Item ${index + 1}`}
          >
            {block.heading || `Item ${index + 1}`}
          </button>
        ))}
      </div>
      <div className="public-report-tabpanel" role="tabpanel">
        <strong>{blocks[activeIndex]?.heading || `Item ${activeIndex + 1}`}</strong>
        <div className="public-report-tabpanel-body">
          {renderBlockItems(blocks[activeIndex]?.items || [], `${sectionKey}-${activeIndex}`)}
        </div>
      </div>
    </div>
  );
}

function parseRecommendationRows(text) {
  const raw = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!raw) return [];
  const blockTexts = raw.includes("### ")
    ? raw.split(/\n(?=### )/).map((part) => part.trim()).filter(Boolean)
    : [raw];
  const rows = [];
  const labelMap = [
    { key: "recommendation", labels: ["recommendation"] },
    { key: "area", labels: ["area"] },
    { key: "priority", labels: ["priority"] },
    { key: "impact", labels: ["impact"] },
    { key: "effort", labels: ["effort"] },
    { key: "suggested_owner", labels: ["suggested owner", "owner"] },
    { key: "reason", labels: ["reason", "expected outcome"] }
  ];

  const matchLabelLine = (line) => {
    const cleaned = line.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "");
    const lower = cleaned.toLowerCase().replace(/\s+/g, " ").trim();
    for (const { key, labels } of labelMap) {
      if (labels.some((label) => lower === `${label}:` || lower.startsWith(`${label}:`))) {
        return key;
      }
    }
    return "";
  };

  const normalizeValue = (value) => String(value || "").replace(/\s+/g, " ").trim();

  blockTexts.forEach((blockText) => {
    const lines = blockText.split("\n");
    const headingLine = lines[0] || "";
    const heading = headingLine.startsWith("### ")
      ? headingLine.replace(/^###\s+/, "").trim()
      : "Recommendation";
    const body = headingLine.startsWith("### ") ? lines.slice(1).join("\n") : lines.join("\n");
    const row = {
      heading,
      recommendation: "",
      area: "",
      priority: "",
      impact: "",
      effort: "",
      suggested_owner: "",
      reason: ""
    };
    let activeKey = "";
    body.split("\n").forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const key = matchLabelLine(trimmed);
      if (key) {
        activeKey = key;
        const afterColon = trimmed.split(":").slice(1).join(":").trim();
        if (afterColon) {
          row[key] = row[key] ? `${row[key]}\n${afterColon}` : afterColon;
        }
        return;
      }
      if (activeKey) {
        row[activeKey] = row[activeKey] ? `${row[activeKey]}\n${trimmed}` : trimmed;
        return;
      }
      if (!row.recommendation) {
        row.recommendation = trimmed;
      }
    });

    Object.keys(row).forEach((key) => {
      if (key === "heading") return;
      row[key] = normalizeValue(row[key]);
    });
    if (!row.recommendation) {
      row.recommendation = heading !== "Recommendation" ? heading : "";
    }

    const hasAny = Object.entries(row).some(([key, value]) => key !== "heading" && String(value || "").trim() !== "");
    if (hasAny) rows.push(row);
  });

  return rows;
}

function normalizeRecommendationRow(item) {
  return {
    heading: item.label || item.heading || "Recommendation",
    recommendation: item.recommendation || "",
    area: item.area || "",
    priority: item.priority || "",
    impact: item.impact || "",
    effort: item.effort || "",
    suggested_owner: item.suggested_owner || "",
    reason: item.reason || ""
  };
}

export default function PublicReportPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get("code") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [executiveTab, setExecutiveTab] = useState(0);

  const pageUrl = useMemo(() => `${window.location.origin}/reports/${slug}`, [slug]);

  const loadReport = async (event) => {
    if (event) event.preventDefault();
    if (!code.trim()) {
      setError("Enter the secret code to view this report.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${apiBaseURL}/public/reports/${slug}`, {
        params: { code: code.trim() }
      });
      setReport(data.data || null);
    } catch (requestError) {
      setReport(null);
      setError(requestError?.response?.data?.message || "Unable to open this report.");
    } finally {
      setLoading(false);
    }
  };

  const sections = decodeSections(report);
  const derivedScore = deriveRiskScoreFromMeta(sections.section_meta);
  const score = Number(sections.risk_score || report?.risk_score || derivedScore || 0);
  const profile = riskProfile(score);
  const reportTitle = report?.report_title || "WordPress Assessment Report";
  const websiteName = report?.website_name || "WordPress Website";
  const websiteUrl = report?.website_url || "";
  const incidents = report?.incidents || [];
  const visibleSections = SECTION_ORDER
    .filter((key) => key !== "executive_summary")
    .map((key) => ({ key, label: SECTION_LABELS[key], value: String(sections[key] || "").trim() }))
    .filter((section) => section.value !== "");

  const incidentStats = useMemo(() => {
    const total = incidents.length;
    const critical = incidents.filter((incident) => {
      const severity = String(incident.severity || "").toLowerCase();
      return severity === "critical" || severity === "high";
    }).length;
    const resolved = incidents.filter((incident) => String(incident.status || "").toLowerCase() === "resolved").length;
    const downtimeValues = incidents
      .map((incident) => Number(incident.downtime_minutes || 0))
      .filter((value) => Number.isFinite(value) && value > 0);
    const avgDowntime = downtimeValues.length
      ? Math.round(downtimeValues.reduce((sum, value) => sum + value, 0) / downtimeValues.length)
      : 0;

    const bySeverity = incidents.reduce((acc, incident) => {
      const severity = String(incident.severity || "unknown").toLowerCase();
      acc[severity] = (acc[severity] || 0) + 1;
      return acc;
    }, {});

    const byStatus = incidents.reduce((acc, incident) => {
      const status = String(incident.status || "open").toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return { total, critical, resolved, avgDowntime, bySeverity, byStatus };
  }, [incidents]);

  const executiveHighlights = useMemo(() => {
    const blocks = parseStructuredBlocks(sections.executive_summary);
    return blocks.slice(0, 6).map((block) => ({
      heading: block.heading,
      items: block.items || []
    })).filter((item) => item.heading || item.items.length);
  }, [sections.executive_summary]);

  const recommendationRowsFromMeta = useMemo(() => {
    const rawRows = sections.section_meta?.recommendation_matrix?._rows;
    if (!Array.isArray(rawRows) || !rawRows.length) return [];
    return rawRows
      .map((item) => normalizeRecommendationRow(item))
      .filter((row) => Object.entries(row).some(([key, value]) => key !== "heading" && String(value || "").trim() !== ""));
  }, [sections.section_meta]);

  useEffect(() => {
    if (executiveTab >= executiveHighlights.length) {
      setExecutiveTab(0);
    }
  }, [executiveHighlights.length, executiveTab]);


  const copyAccess = async () => {
    try {
      await navigator.clipboard.writeText(`Report link: ${pageUrl}\nSecret code: ${code}`);
    } catch {
      setError("Unable to copy access details from this browser.");
    }
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(report?.report_title || "WordPress Assessment Report");
    const body = encodeURIComponent(`Report link: ${pageUrl}\nSecret code: ${code}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareAccess = async () => {
    if (!navigator.share) {
      await copyAccess();
      return;
    }

    try {
      await navigator.share({
        title: report?.report_title || "WordPress Assessment Report",
        text: `Secret code: ${code}`,
        url: pageUrl
      });
    } catch {
      // User canceled share.
    }
  };

  const exportPdf = () => {
    window.print();
  };

  return (
    <main className="page-grid public-report-page">
      <section className="card-modern public-report-hero">
        <div className="public-report-hero-copy">
          <p className="tool-permalink">Client Assessment Report</p>
          <h2>{reportTitle}</h2>
          <p className="muted">
            A focused review of stability, governance, and operational risk for the website.
          </p>
          {report ? (
            <div className="public-report-action-strip">
              <button type="button" className="button-link ghost compact-action-btn" onClick={copyAccess}>Copy</button>
              <button type="button" className="button-link ghost compact-action-btn" onClick={shareByEmail}>Email</button>
              <button type="button" className="button-link ghost compact-action-btn" onClick={shareAccess}>Share</button>
              <button type="button" className="button-link compact-action-btn" onClick={exportPdf}>Print / Save PDF</button>
            </div>
          ) : null}
          <div className="public-report-hero-meta">
            {websiteName ? <span>{websiteName}</span> : null}
            {websiteUrl ? <span>{websiteUrl}</span> : null}
            {report?.submitted_at ? <span>Prepared {normalizeDate(report.submitted_at)}</span> : null}
          </div>
        </div>
        <form className="public-report-access" onSubmit={loadReport}>
          <div className="public-report-access-head">
            <strong>Secure Access</strong>
            <small>Enter the private code shared with your team.</small>
          </div>
          <div className="public-report-access-row">
            <input type="password" value={code} onChange={(event) => setCode(event.target.value)} placeholder="Secret code" />
            <button type="submit" disabled={loading}>{loading ? "Opening..." : "Open"}</button>
          </div>
        </form>
      </section>

      {error ? <section className="card-modern"><p className="error">{error}</p></section> : null}

      {report ? (
        <>
          <section className="card-modern public-report-print-cover">
            <p className="tool-permalink">WordPress Website Assessment Report</p>
            <h1>{reportTitle}</h1>
            {websiteName ? <p><strong>Client:</strong> {websiteName}</p> : null}
            {websiteUrl ? <p><strong>Website:</strong> {websiteUrl}</p> : null}
            {report?.submitted_at ? <p><strong>Prepared on:</strong> {normalizeDate(report.submitted_at)}</p> : null}
            <p><strong>Overall Risk Score:</strong> {score}/100 ({profile.label})</p>
          </section>

          <section className="public-report-overview">
            <article className="card-modern public-report-scorecard">
              <div className={`report-score-ring report-score-${profile.tone}`} style={{ "--score": `${Math.max(0, Math.min(100, score))}%` }}>
                <div>
                  <strong>{score}</strong>
                  <span>/100</span>
                </div>
              </div>
              <div className="public-report-score-copy">
                <p className="tool-permalink">Overall Risk</p>
                <h3>{profile.label}</h3>
              </div>
            </article>
            <section className="card-modern public-report-context-card">
              <p className="tool-permalink">Incident Snapshot</p>
              <h3>{incidentStats.total} Recorded Incident{incidentStats.total === 1 ? "" : "s"}</h3>
              <div className="public-report-snapshot-metrics">
                <span>Critical/High: <strong>{incidentStats.critical}</strong></span>
                <span>Resolved: <strong>{incidentStats.resolved}</strong></span>
                <span>Avg Downtime: <strong>{incidentStats.avgDowntime}</strong> mins</span>
              </div>
              {incidentStats.total ? (
                <div className="public-report-pies">
                  <div className="pie-card">
                    <div
                      className="pie-chart"
                      style={{
                        "--slice-a": `${Math.round(((incidentStats.bySeverity?.critical || 0) / incidentStats.total) * 360)}deg`,
                        "--slice-b": `${Math.round(((incidentStats.bySeverity?.high || 0) / incidentStats.total) * 360)}deg`,
                        "--slice-c": `${Math.round(((incidentStats.bySeverity?.medium || 0) / incidentStats.total) * 360)}deg`
                      }}
                    />
                    <div>
                      <strong>Severity Mix</strong>
                      <small>Critical/High/Medium/Low</small>
                    </div>
                  </div>
                  <div className="pie-card">
                    <div
                      className="pie-chart pie-chart-status"
                      style={{
                        "--slice-a": `${Math.round(((incidentStats.byStatus?.resolved || 0) / incidentStats.total) * 360)}deg`,
                        "--slice-b": `${Math.round(((incidentStats.byStatus?.investigating || 0) / incidentStats.total) * 360)}deg`
                      }}
                    />
                    <div>
                      <strong>Status Mix</strong>
                      <small>Resolved/Investigating/Open</small>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="public-report-severity-bars">
                {["critical", "high", "medium", "low"].map((level) => (
                  <div key={level} className={`severity-bar severity-${level}`}>
                    <span>{level}</span>
                    <div>
                      <i style={{ width: `${incidentStats.total ? Math.round(((incidentStats.bySeverity?.[level] || 0) / incidentStats.total) * 100) : 0}%` }} />
                    </div>
                    <strong>{incidentStats.bySeverity?.[level] || 0}</strong>
                  </div>
                ))}
              </div>
            </section>
          </section>

          {incidents.length ? (
            <section className="card-modern">
              <div className="section-head">
                <div>
                  <p className="tool-permalink">Incidents</p>
                  <h3>Incident Register</h3>
                </div>
              </div>
              <div className="public-report-table-wrap">
                <table className="public-report-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Occurred</th>
                      <th>Downtime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incidents.map((incident) => (
                      <tr key={incident.id}>
                        <td>{incident.title || "-"}</td>
                        <td>{incident.severity || "-"}</td>
                        <td>{incident.status || "-"}</td>
                        <td>{normalizeDate(incident.occurred_at)}</td>
                        <td>{incident.downtime_minutes ? `${incident.downtime_minutes} min` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section className="card-modern">
            <div className="section-head">
              <div>
                <p className="tool-permalink">Report Content</p>
                <h3>Assessment Narrative</h3>
              </div>
            </div>
            {!visibleSections.length ? (
              <p className="muted">No section data is available in this submitted report yet.</p>
            ) : (
              <div className="public-report-dynamic-sections">
                {executiveHighlights.length ? (
                  <article className="public-report-dynamic-card public-report-dynamic-executive">
                    <div className="public-report-dynamic-head">
                      <span>01</span>
                      <h4>Executive Summary</h4>
                    </div>
                    <div className="public-report-dynamic-body">
                      <div className="public-report-executive-tabs">
                        <div className="public-report-executive-tablist" role="tablist" aria-label="Executive summary tabs">
                          {executiveHighlights.map((item, index) => (
                            <button
                              key={`${item.heading}-${index}`}
                              type="button"
                              className={`executive-tab ${executiveTab === index ? "active" : ""}`}
                              onClick={() => setExecutiveTab(index)}
                              role="tab"
                              aria-selected={executiveTab === index}
                              title={item.heading || `Summary ${index + 1}`}
                            >
                              {item.heading || `Summary ${index + 1}`}
                            </button>
                          ))}
                        </div>
                        <div className="public-report-executive-panel" role="tabpanel">
                          <strong>{executiveHighlights[executiveTab]?.heading || `Summary ${executiveTab + 1}`}</strong>
                          <div className="public-report-executive-body">
                            {renderBlockItems(executiveHighlights[executiveTab]?.items || [], `exec-${executiveTab}`)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ) : null}
                {visibleSections.map((section, sectionIndex) => {
                  const blocks = parseStructuredBlocks(section.value);
                  const recommendationRows = section.key === "recommendation_matrix"
                    ? (recommendationRowsFromMeta.length ? recommendationRowsFromMeta : parseRecommendationRows(section.value))
                    : [];
                  const tabbedSections = new Set([
                    "positive_signals",
                    "current_risks",
                    "incident_learnings",
                    "process_findings_by_area",
                    "operational_readiness",
                    "recommendations",
                    "consultant_notes"
                  ]);
                  const indexLabel = executiveHighlights.length
                    ? sectionIndex + 2
                    : sectionIndex + 1;
                  return (
                    <article key={section.key} className="public-report-dynamic-card">
                      <div className="public-report-dynamic-head">
                        <span>{String(indexLabel).padStart(2, "0")}</span>
                        <h4>{section.label}</h4>
                      </div>
                      <div className="public-report-dynamic-body">
                        {section.key === "recommendation_matrix" && recommendationRows.length ? (
                          <div className="public-report-matrix-table-wrap">
                            <table className="public-report-matrix-table">
                              <thead>
                                <tr>
                                  <th>Recommendation</th>
                                  <th>Area</th>
                                  <th>Priority</th>
                                  <th>Impact</th>
                                  <th>Effort</th>
                                  <th>Owner</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recommendationRows.map((row, rowIndex) => (
                                  <tr key={`matrix-${rowIndex}`}>
                                    <td>
                                      <strong>{row.recommendation || row.heading || "-"}</strong>
                                      {row.reason ? <span>{row.reason}</span> : null}
                                    </td>
                                    <td>{row.area || "-"}</td>
                                    <td>{row.priority || "-"}</td>
                                    <td>{row.impact || "-"}</td>
                                    <td>{row.effort || "-"}</td>
                                    <td>{row.suggested_owner || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : tabbedSections.has(section.key) && blocks.length > 1 ? (
                          <TabbedSection blocks={blocks} sectionKey={section.key} />
                        ) : (
                          blocks.map((block, blockIndex) => (
                            <div key={`${section.key}-block-${blockIndex}`} className="public-report-block">
                              <h5>{block.heading}</h5>
                              {renderBlockItems(block.items, `${section.key}-${blockIndex}`)}
                            </div>
                          ))
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}
